import { Injectable, inject } from '@angular/core';
import { IdbService } from './idb.service';

/**
 * Représentation interne d'une entrée du cache offline.
 */
export interface CacheEntry<T = unknown> {
  /** Clé primaire IDB : "type:url" (ex: "GET:/api/v1/client/search?..."). */
  key: string;
  /** Catégorie logique pour purge/stats : "client", "mesure", etc. */
  type: string;
  /** Charge utile sérialisable (typiquement la réponse JSON du backend). */
  payload: T;
  /** Timestamp UTC de la mise en cache. */
  updatedAt: number;
  /** TTL en millisecondes — au-delà l'entrée est considérée comme stale. */
  ttlMs: number;
}

/**
 * Cache des réponses GET fréquemment lues, persisté dans IndexedDB.
 *
 * <p>Stratégie : on stocke chaque réponse HTTP GET indexée par méthode + URL
 * (clé unique). Le {@link OfflineHttpInterceptor} alimente ce cache à
 * chaque requête réussie, et le sert quand le réseau est down.</p>
 *
 * <p>TTL conservatif (24h par défaut) — un atelier qui passe quelques
 * jours offline peut continuer à consulter ses derniers clients/mesures/
 * commandes même si les données sont un peu datées.</p>
 *
 * <p>Limite : on ne fait pas d'invalidation transactionnelle. Si une
 * mutation locale change une entité, l'entrée correspondante doit être
 * invalidée explicitement par l'intercepteur (méthode {@link #invalidateByType}).</p>
 */
@Injectable({ providedIn: 'root' })
export class CacheStore {
  private idb = inject(IdbService);

  /** TTL par défaut : 24h. */
  static readonly DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

  /** Met en cache la réponse d'une requête GET. */
  async put<T>(key: string, type: string, payload: T, ttlMs?: number): Promise<void> {
    const entry: CacheEntry<T> = {
      key,
      type,
      payload,
      updatedAt: Date.now(),
      ttlMs: ttlMs ?? CacheStore.DEFAULT_TTL_MS,
    };
    try {
      await this.idb.put(IdbService.STORE_CACHE, entry);
    } catch {
      // Cache best-effort — on n'échoue jamais la requête utilisateur à cause de l'IDB.
    }
  }

  /** Récupère une entrée si présente et non expirée. */
  async get<T>(key: string): Promise<T | undefined> {
    try {
      const entry = await this.idb.get<CacheEntry<T>>(IdbService.STORE_CACHE, key);
      if (!entry) return undefined;
      if (this.isExpired(entry)) return undefined;
      return entry.payload;
    } catch {
      return undefined;
    }
  }

  /**
   * Récupère une entrée même si elle est expirée — utile en mode offline,
   * où mieux vaut une donnée stale que pas de donnée du tout.
   */
  async getStale<T>(key: string): Promise<{ payload: T; updatedAt: number } | undefined> {
    try {
      const entry = await this.idb.get<CacheEntry<T>>(IdbService.STORE_CACHE, key);
      if (!entry) return undefined;
      return { payload: entry.payload, updatedAt: entry.updatedAt };
    } catch {
      return undefined;
    }
  }

  /** Liste les entrées d'un type (debug + page Sync). */
  async listByType(type: string): Promise<CacheEntry[]> {
    try {
      return await this.idb.getAll<CacheEntry>(IdbService.STORE_CACHE, 'byType', type);
    } catch {
      return [];
    }
  }

  /** Liste tout — pour la page Sync. */
  async listAll(): Promise<CacheEntry[]> {
    try {
      return await this.idb.getAll<CacheEntry>(IdbService.STORE_CACHE);
    } catch {
      return [];
    }
  }

  /** Compte les entrées en cache. */
  async size(): Promise<number> {
    try {
      return await this.idb.count(IdbService.STORE_CACHE);
    } catch {
      return 0;
    }
  }

  /** Compte par type (pour la page Sync). */
  async sizeByType(): Promise<Record<string, number>> {
    const all = await this.listAll();
    const map: Record<string, number> = {};
    for (const e of all) {
      map[e.type] = (map[e.type] || 0) + 1;
    }
    return map;
  }

  /**
   * Invalide toutes les entrées d'un type donné — à appeler après une
   * mutation pour forcer un rechargement depuis le backend dès qu'il est
   * disponible.
   */
  async invalidateByType(type: string): Promise<void> {
    try {
      const entries = await this.listByType(type);
      for (const e of entries) {
        await this.idb.delete(IdbService.STORE_CACHE, e.key);
      }
    } catch {
      // best-effort
    }
  }

  /** Purge complète du cache. */
  async clear(): Promise<void> {
    try {
      await this.idb.clear(IdbService.STORE_CACHE);
    } catch {
      // best-effort
    }
  }

  /** Purge des entrées expirées (à appeler ponctuellement). */
  async purgeExpired(): Promise<number> {
    try {
      const all = await this.listAll();
      let removed = 0;
      for (const e of all) {
        if (this.isExpired(e)) {
          await this.idb.delete(IdbService.STORE_CACHE, e.key);
          removed++;
        }
      }
      return removed;
    } catch {
      return 0;
    }
  }

  // ─── helpers ──────────────────────────────────────────────────────────

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.updatedAt > entry.ttlMs;
  }

  /**
   * Devine le "type" logique d'une URL — pour catégoriser les entrées
   * et permettre l'invalidation ciblée.
   *
   * Convention : on prend le 1er segment significatif après /api/v1/.
   * Exemple : /api/v1/client/search → "client".
   */
  static typeFromUrl(url: string): string {
    try {
      const u = new URL(url, 'http://localhost');
      const m = u.pathname.match(/\/api\/v\d+\/([^/?]+)/);
      return m ? m[1] : 'misc';
    } catch {
      return 'misc';
    }
  }
}
